<script setup>
import {Link, router, useForm} from '@inertiajs/vue3';
import InputError from '@/Components/InputError.vue';
import Socialstream from '@/Components/Socialstream.vue';
import AppLayout from "@/Layouts/AppLayout.vue";
import {computed, inject, ref} from "vue";
import {route} from "ziggy-js";
import {useGoogleRecaptcha} from "@/plugins/recaptcha.js";
import axios from 'axios';
import AkLogo from "@/Components/Icons/AkLogo.vue";
import Checkbox from "@/Components/Checkbox.vue"; // Добавляем импорт axios
import {trans} from "laravel-vue-i18n";
import {reachYaGoal} from "@/helper.js";

const props = defineProps({
    canResetPassword: Boolean,
    status: String,
    login_reason: {
        type: String,
        default: 'default',
    },
});

const loginReasonText = computed(() => {
    switch (props.login_reason) {
        case 'order':
            return trans('Log in to place your order');
        case 'chat':
            return trans('You need to log in to enter the chat');
        case 'offer':
            return trans('Log in to list your item for sale');
        default:
            return trans('Sign in to your AnyKod account');
    }
});

// Управление шагами процесса авторизации
const activeStep = ref('email'); // email, requestCode, enterCode
const emailValue = ref('');
const verificationCode = ref('');
const processingRequest = ref(false);
const validationError = ref(null);
const serverError = ref(null); // Для хранения ошибок с сервера
const timer = ref(null);
const dataTimer = ref(0);

const form = useForm({
    email: '',
    g_captcha_response: ''
});

const formVerify = useForm({
    email: '',
    code: '',
    g_captcha_response: ''
});

const updateChannel = inject('updateChannel'),
    updateMessageChannel = inject('updateMessageChannel')

const {getRecaptchaToken} = useGoogleRecaptcha()

// Валидация email на фронтенде
const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

const startTimer = () => {
    dataTimer.value = 60
    timer.value = setInterval(() => {
        --dataTimer.value;
        if (dataTimer.value <= 0) {
            clearTimeout(timer.value)
        }
    }, 1000)
}

// Переход к запросу кода после валидации email
const validateAndContinue = () => {
    if (!validateEmail(form.email)) {
        validationError.value = "Incorrect email address. Try again.";
        return;
    }

    emailValue.value = form.email;
    validationError.value = null;
    activeStep.value = 'requestCode';
};

const validatePasswordFormAndContinue = () => {
    if (!validateEmail(passwordForm.email)) {
        validationError.value = 'Incorrect email address. Try again.';
        return;
    }

    emailValue.value = passwordForm.email;
    validationError.value = null;

    submitPassword();
};

// Запрос кода на email с использованием axios вместо form.post
const sendCode = async () => {
    processingRequest.value = true;
    serverError.value = null;

    try {
        const captchaToken = await getRecaptchaToken();
        const response = await axios.post(route('login.send-code'), {
            email: form.email,
            g_captcha_response: captchaToken
        });

        // При успешном ответе переходим к вводу кода
        activeStep.value = 'enterCode';
        formVerify.email = form.email;
        dataTimer.value = 60
        startTimer()
    } catch (error) {
        // Обработка ошибок сервера
        if (error.response && error.response.data) {
            const responseData = error.response.data;

            if (responseData.errors) {
                // Показываем ошибки валидации
                if (responseData.errors.email) {
                    serverError.value = responseData.errors.email[0];
                } else if (responseData.errors.g_captcha_response) {
                    serverError.value = trans('Captcha verification error. Try again.')
                } else {
                    serverError.value = trans('There was an error sending the code.')
                }
            } else if (responseData.message) {
                serverError.value = responseData.message;
            }
        } else {
            serverError.value = trans('There was an error sending your request.')
        }
    } finally {
        processingRequest.value = false;
    }
};

// Проверка введенного кода с использованием axios
const verifyCode = async () => {
    processingRequest.value = true;
    serverError.value = null;

    try {
        const captchaToken = await getRecaptchaToken();
        const response = await axios.post(route('login.verify-code'), {
            email: formVerify.email,
            code: verificationCode.value,
            g_captcha_response: captchaToken
        });
        console.log(response)
        reachYaGoal('login-completed')
        if (response.data.redirect) {
            window.location.href = response.data.redirect;
        } else {
            router.get(route('home'));
        }
    } catch (error) {
        // Обработка ошибок сервера
        if (error.response && error.response.data) {
            const responseData = error.response.data;

            if (responseData.errors) {
                // Показываем ошибки валидации
                if (responseData.errors.code) {
                    serverError.value = responseData.errors.code[0];
                } else if (responseData.errors.email) {
                    serverError.value = responseData.errors.email[0];
                } else if (responseData.errors.g_captcha_response) {
                    serverError.value = trans('Captcha verification error. Try again.')
                } else {
                    serverError.value = trans('There was an error checking the code.')
                }
            } else if (responseData.message) {
                serverError.value = responseData.message;
            }
        } else {
            serverError.value = trans('There was an error sending your request.')
        }
    } finally {
        processingRequest.value = false;
    }
};

// Изменить email
const changeEmail = () => {
    activeStep.value = 'email';
    serverError.value = null;
};

// Показать форму входа по паролю
const showPasswordForm = () => {
    activeStep.value = 'password';
    serverError.value = null;

    // Инициализация формы пароля если не существует
    if (!passwordForm) {
        passwordForm = useForm({
            email: emailValue.value,
            password: '',
            remember: false,
            g_captcha_response: ''
        });
    } else {
        // Обновить email, если он изменился
        passwordForm.email = emailValue.value;
    }
};

// Показать форму входа по коду
const showCodeForm = () => {
    activeStep.value = 'requestCode';
    serverError.value = null;
};

// Переключение видимости пароля
const showPassword = ref(false);
const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value;
};

// Форма для входа по паролю
let passwordForm = useForm({
    email: '',
    password: '',
    remember: false,
    g_captcha_response: ''
});
const passwordError = ref(null);

// Отправка формы входа по паролю
const submitPassword = async () => {
    passwordError.value = null;

    try {
        const captchaToken = await getRecaptchaToken();
        passwordForm.g_captcha_response = captchaToken;

        passwordForm.post(route('login'), {
            onSuccess: () => {
                reachYaGoal('login-completed')
                router.get(route('home'));
            },
            onError: (errors) => {
                if (errors.email) {
                    passwordError.value = errors.email;
                } else if (errors.password) {
                    passwordError.value = errors.password;
                } else if (errors.g_captcha_response) {
                    passwordError.value = 'Captcha verification error. Try again.';
                }
            }
        });
    } catch (error) {
        passwordError.value = 'There was an error sending your request.';
    }
};
</script>

<template>
    <AppLayout :title="$t('Login')">
        <div class="flex justify-center items-center bg-[#F5F5F5] dark:bg-deep-blue px-2">
            <div
                class="w-full max-w-[340px] lg:max-w-[680px] bg-white dark:bg-dark-blue rounded-lg shadow-[0_2px_6px_0_rgba(0,0,0,0.06)] flex items-stretch gap-3 p-0">
                <div class="flex w-full lg:w-1/2 flex-col gap-3 px-3 pt-5 pb-4">
                    <!-- Шаг 1: Ввод Email -->
                    <form v-if="activeStep === 'email'" class="flex flex-col gap-[12px]"
                          @submit.prevent="validateAndContinue">
                        <div class="px-0.5 gap-2.5 flex items-center mb-3">
                            <ion-icon class="text-blue-600" name="alert-circle" style="width: 16px; height: 16px"></ion-icon>
                            <p class="text-blue-600 font-sans font-medium text-sm">
                                {{ loginReasonText }}
                            </p>
                        </div>

                        <div class="relative flex items-center justify-center dark:bg-slate-500 rounded-lg bg-slate-300 py-0.5">
                            <h2 class="text-xl font-semibold text-center">{{ $t('Login') }}</h2>
                        </div>

                        <div class="mb-2">
                            <p class="text-base px-2.5 text-black leading-[1.2] dark:text-slate-200">
                                {{ $t('We\'ll send a login code to your email') }}</p>
                        </div>

                        <div
                            class="flex items-center border border-[#CBD5E1] rounded-[8px] h-[40px] px-[17px] bg-white dark:bg-transparent">
                            <ion-icon class="w-5 h-5 text-[#9CA3AF] mr-2" name="mail-outline"></ion-icon>
                            <input
                                id="email"
                                v-model="form.email"
                                :placeholder="$t('Enter email')"
                                autocomplete="email"
                                class="w-full bg-transparent dark:text-slate-200 focus:ring-0 dark:bg-transparent outline-hidden border-0 text-base placeholder-[#9CA3AF]"
                                required
                                type="email"
                            />
                        </div>
                        <InputError v-if="validationError" :message="$t(validationError)" class="mt-1"/>

                        <div class="mt-2 flex justify-center">
                            <button
                                class="px-8 h-[40px] flex items-center justify-center bg-blue-600 hover:bg-blue-300 dark:hover:bg-blue-500 disabled:bg-blue-300 dark:disabled:bg-blue-500 text-white text-[14px] font-medium rounded-full uppercase transition"
                                type="submit"
                                :disabled="!form.email"
                            >
                                {{ $t('Next') }}
                            </button>
                        </div>

                        <p class="text-xs text-center text-slate-500 dark:text-slate-400 px-2.5">
                            {{ $t('By continuing, you agree to the') }}
                            <Link :href="route('pages.show', {slug: 'terms-of-use'})"
                                  class="underline hover:text-slate-700 dark:hover:text-slate-300">{{ $t('Terms of Use') }}</Link>
                            {{ $t('and') }}
                            <Link :href="route('pages.show', {slug: 'privacy-policy'})"
                                  class="underline hover:text-slate-700 dark:hover:text-slate-300">{{ $t('Privacy Policy') }}</Link>
                        </p>

                        <!-- Соц. вход -->
                        <div v-if="$page.props.socialstream?.show" class="flex flex-col gap-2 mt-4">
                            <Socialstream/>
                        </div>
                    </form>

                    <!-- Шаг 2: Запрос кода -->
                    <div v-else-if="activeStep === 'requestCode'" class="flex flex-col gap-[12px] pb-4">
                        <!-- Назад -->
                        <div class="flex items-center gap-1 mb-2">
                            <button
                                class="flex items-center text-[#21272A] font-medium px-2.5"
                                type="button"
                                @click="changeEmail"
                            >
                                <ion-icon class="w-5 h-5 dark:text-slate-200" name="chevron-back"></ion-icon>
                                <span class="text-[14px] dark:text-slate-200">{{ $t('Back') }}</span>
                            </button>
                        </div>

                        <!-- Email -->
                        <div class="mb-2">
                            <h2 class="text-xl px-2.5 font-semibold dark:text-slate-200">{{ emailValue }}</h2>
                        </div>

                        <!-- Инструкции -->
                        <div class="text-base text-black px-2.5 mb-2 dark:text-slate-200">
                            {{ $t('We’ll send the login code to ') }}<br><b>{{ emailValue }}</b>
                        </div>

                        <!-- Получить код -->
                        <div class="flex justify-center gap-2 mt-2 border-b border-dashed pb-4 border-slate-300">
                            <button
                                :disabled="processingRequest"
                                class="px-8 w-min h-[40px] flex items-center justify-center bg-blue-600 text-white whitespace-nowrap text-sm font-medium rounded-full uppercase hover:opacity-50 disabled:opacity-50 transition"
                                @click="sendCode"
                            >
                                <span v-if="processingRequest">{{ $t('Sending...') }}</span>
                                <span v-else>{{ $t('Send code') }}</span>
                            </button>
                        </div>

                        <!-- Или -->
                        <div class="flex justify-center ">
                            <span class="text-[14px] text-[#131933] dark:text-slate-200">{{ $t('or') }}</span>
                        </div>

                        <!-- Войти с паролем -->
                        <div class="flex justify-center">
                            <button
                                :disabled="processingRequest"
                                class="text-blue-600 border-b text-sm hover:opacity-50 border-blue-600 text-base leading-[14px] font-medium"
                                type="button"
                                @click="showPasswordForm"
                            >
                                {{ $t('Login with password') }}
                            </button>
                        </div>

                        <div v-if="serverError" class="mt-2 text-red-600 text-sm text-center">{{ serverError }}</div>
                    </div>

                    <!-- Шаг 3: Ввод кода подтверждения -->
                    <form v-else-if="activeStep === 'enterCode'" class="flex flex-col gap-[12px]"
                          @submit.prevent="verifyCode">
                        <!-- Назад -->
                        <div class="flex items-center gap-1 mb-2">
                            <button
                                class="flex items-center text-[#21272A] dark:text-slate-500 font-medium"
                                type="button"
                                @click="changeEmail"
                            >
                                <ion-icon class="w-5 h-5" name="chevron-back"></ion-icon>
                                <span class="text-sm] dark:text-slate-500">{{ $t('Back') }}</span>
                            </button>
                        </div>

                        <!-- Email -->
                        <h2 class="text-base px-2.5 font-medium dark:text-slate-200">{{ $t("Enter code") }}</h2>

                        <!-- Инструкции -->
                        <div class="text-base px-2.5 text-black dark:text-slate-200">
                            {{ $t("We\'ve sent the code to :email. Enter it in the box below:", {
                              email: emailValue
                            })}}
                        </div>

                        <div class="flex flex-col gap-2">
                            <div
                                class="flex items-center border border-[#CBD5E1] rounded-[8px] h-[40px] px-[17px] bg-transparent">
                                <ion-icon class="w-5 h-5 text-[#9CA3AF] mr-2" name="key-outline"></ion-icon>
                                <input
                                    id="verification-code"
                                    v-model="verificationCode"
                                    :placeholder="$t('Enter the code here')"
                                    autocomplete="one-time-code"
                                    class="flex-1 bg-transparent dark:text-slate-200 focus:ring-0 outline-hidden border-0 text-base placeholder-[#9CA3AF]"
                                    inputmode="numeric"
                                    pattern="[0-9]*"
                                    required
                                    type="text"
                                />
                            </div>
                            <InputError v-if="serverError" :message="$t(serverError)" class="mt-1"/>
                        </div>

                        <div class="w-full flex justify-center">
                            <button
                                :disabled="processingRequest || !verificationCode"
                                class="px-8 w-min h-[40px] flex items-center justify-center bg-[#2563EB] text-white text-[14px] font-medium rounded-full uppercase hover:opacity-50 disabled:opacity-50 transition mt-2"
                                type="submit"
                            >
                                <span v-if="processingRequest">{{ $t('Login...') }}</span>
                                <span v-else>{{ $t('Login') }}</span>
                            </button>
                        </div>

                        <!-- Повторно отправить -->
                        <div class="flex mt-1 px-2.5 flex-col pb-2">
                            <p class="font-sans text-sm text-slate-500">
                                {{$t("No email? Have you checked your SPAM folder?")}}
                            </p>
                            <button
                                v-if="!dataTimer"
                                :disabled="processingRequest"
                                class="text-sm whitespace-nowrap w-min text-blue-600 border-b border-blue-600"
                                type="button"
                                @click="sendCode"
                            >
                                {{ $t('Send again') }}
                            </button>
                            <p v-else class="font-sans text-sm text-slate-500" >
                                {{$t("Send again in :time seconds.", {
                                  time: dataTimer
                                })}}
                            </p>
                        </div>
                    </form>

                    <!-- Шаг 4: Вход по паролю -->
                    <form v-else-if="activeStep === 'password'" class="flex flex-col gap-[12px]"
                          @submit.prevent="validatePasswordFormAndContinue">
                        <!-- Назад -->
                        <div class="flex items-center gap-1 mb-2">
                            <button
                                class="flex items-center text-[#21272A] font-medium dark:text-slate-500"
                                type="button"
                                @click="changeEmail"
                            >
                                <ion-icon class="w-5 h-5" name="chevron-back"></ion-icon>
                                <span class="text-sm dark:text-slate-500">{{ $t('Back') }}</span>
                            </button>
                        </div>

                        <!-- Email -->
                        <h2 class="text-base px-2.5 font-medium dark:text-slate-200">{{ $t("Login to an existing account") }}</h2>


                        <div class="flex flex-col gap-3 mt-2">
                            <label for="email" class="px-2.5 font-sans text-sm dark:text-slate-200">
                                Email
                            </label>
                            <div
                                class="flex items-center border border-[#CBD5E1] rounded-[8px] h-[40px] px-[17px] bg-white dark:bg-transparent">
                                <ion-icon class="w-5 h-5 text-[#9CA3AF] mr-2" name="mail-outline"></ion-icon>
                                <input
                                    id="email"
                                    v-model="passwordForm.email"
                                    :placeholder="$t('Enter email')"
                                    autocomplete="email"
                                    class="w-full bg-transparent dark:text-slate-200 focus:ring-0 dark:bg-transparent outline-hidden border-0 text-base placeholder-[#9CA3AF]"
                                    required
                                    type="email"
                                />
                            </div>
                            <InputError v-if="validationError" :message="$t(validationError)" class="mt-1"/>
                            <!-- Пароль -->
                            <label for="password" class="px-2.5 font-sans text-sm dark:text-slate-200">
                                {{$t("Password")}}
                            </label>
                            <div
                                class="flex items-center border border-[#CBD5E1] rounded-lg h-[40px] px-[17px] bg-transparent">
                                <ion-icon class="w-5 h-5 text-[#9CA3AF] mr-2" name="lock-closed-outline"></ion-icon>
                                <input
                                    id="password"
                                    v-model="passwordForm.password"
                                    :placeholder="$t('Enter password')"
                                    :type="showPassword ? 'text' : 'password'"
                                    autocomplete="current-password"
                                    class="flex-1 bg-transparent outline-hidden dark:text-slate-200 focus:ring-0 border-0 text-base placeholder-[#9CA3AF]"
                                    required
                                />
                                <button
                                    class="focus:outline-hidden flex items-center"
                                    type="button"
                                    @click="togglePasswordVisibility"
                                >
                                    <ion-icon
                                        :name="showPassword ? 'eye-outline' : 'eye-off-outline'"
                                        class="w-5 h-5 text-[#9CA3AF]"
                                    ></ion-icon>
                                </button>
                            </div>
                            <InputError v-if="passwordError" :message="$t(passwordError)" class="mt-1"/>
                            <InputError v-if="passwordError" :message="$t('If you are logging in for the first time, click “Back” and log in using the code sent to your email. Later, you will be able to set a password in your personal account settings on the website.')" class="mt-1"/>
                        </div>

                        <!-- Запомнить меня -->
                        <div class="block mt-4">
                            <label class="flex items-center">
                                <Checkbox v-model:checked="passwordForm.remember" name="remember">
                                    <template #label>
                                        <span class="text-sm text-gray-600">{{ $t('Remember me') }}</span>
                                    </template>
                                </Checkbox>
                            </label>
                        </div>

                        <!-- Кнопка Войти -->
                        <div class="flex items-center w-full justify-between">
                            <div class="flex justify-center mt-1">
                                <Link v-if="canResetPassword" :href="route('password.request')"
                                      class="dark:text-slate-500 border-b border-slate-500 leading-[14px] text-sm font-medium"
                                >
                                    {{ $t('Forgot password?') }}
                                </Link>
                            </div>
                            <button
                                :disabled="passwordForm.processing || (!passwordForm.email || !passwordForm.password)"
                                class="w-min px-8 whitespace-nowrap h-[40px] flex items-center justify-center bg-[#2563EB] text-white text-[14px] font-medium rounded-full uppercase mt-2 hover:opacity-50 disabled:opacity-50 transition"
                                type="submit"
                            >
                                <span v-if="passwordForm.processing">{{ $t('Login...') }}</span>
                                <span v-else>{{ $t('Login') }}</span>
                            </button>
                        </div>


                        <!-- Забыли пароль -->


                        <!-- Войти по коду -->
                        <div v-if="$page.props.socialstream?.show" class="flex flex-col gap-2 mt-4">
                            <Socialstream/>
                        </div>
                    </form>
                </div>
                <div class="hidden lg:flex w-1/2 justify-center items-center bg-slate-50 dark:bg-dark-blue">
                    <AkLogo class="w-[220px] h-[256px]"/>
                </div>
            </div>
        </div>
    </AppLayout>
</template>
